/**
 * ProjectGallery — full-bleed image carousel for a single project.
 *
 * Web projects:
 *   Swiper carousel with freeMode + autoplay (delay: 0) for a continuous
 *   marquee effect. Each slide contains a device frame and an optional
 *   feature text block that animates with Swiper's built-in parallax.
 *   Inactive slides are dimmed; the active (centred) slide is full opacity.
 *   Click a slide to open the expanded overlay.
 *
 * Print projects (projectType contains "print"):
 *   Plain image grid — no device frames.
 */
import "swiper/css"
import { getImageUrl } from "@/lib/images"
import { globalLenis } from "@/lib/lenis"
import type { ProjectImage } from "@/types/portfolio"
import { ArrowLeft, ArrowRight, X } from "@phosphor-icons/react"
import gsap from "gsap"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, FreeMode, Mousewheel, Parallax } from "swiper/modules"
import { DeviceFrame, FRAME_PADDING, type DeviceType } from "./DeviceFrame"

export const CAROUSEL_H = 420
const FEATURE_TEXT_H = 80 // space below carousel for per-slide labels

// Pre-computed slide widths based on device aspect ratio × carousel height
export const SLIDE_WIDTHS: Record<DeviceType, number> = {
  desktop: Math.round(CAROUSEL_H * 16 / 9),  // 747
  generic: Math.round(CAROUSEL_H * 4 / 3),   // 560
  tablet:  Math.round(CAROUSEL_H * 3 / 4),   // 315
  mobile:  Math.round(CAROUSEL_H * 9 / 16),  // 236
  print:   CAROUSEL_H,                        // 420 (1:1 square)
}

// ─── PrintGallery ─────────────────────────────────────────────────────────────

function PrintGallery({ images }: { images: ProjectImage[] }) {
  return (
    <div data-print-gallery className="mx-auto max-w-6xl px-6 md:px-10">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {images.map((img) => {
          const url = getImageUrl(img, "large")
          if (!url) return null
          return img.ext === "webm" || img.ext === "mp4" ? (
            <video
              key={img.id}
              src={url}
              className="block h-auto w-full rounded-sm"
              style={{ filter: "contrast(1.04) brightness(0.94)" }}
              autoPlay
              loop
              muted
              playsInline
            />
          ) : (
            <img
              key={img.id}
              src={url}
              alt=""
              className="block h-auto w-full rounded-sm"
              style={{ filter: "contrast(1.04) brightness(0.94)" }}
              loading="lazy"
              decoding="async"
            />
          )
        })}
      </div>
    </div>
  )
}

// ─── ExpandedOverlay ──────────────────────────────────────────────────────────

export interface ExpandedOverlayProps {
  items: CarouselItem[]
  initialIdx: number
  onClose: () => void
}

export function ExpandedOverlay({ items, initialIdx, onClose }: ExpandedOverlayProps) {
  const overlayRef   = useRef<HTMLDivElement>(null)
  const contentRef   = useRef<HTMLDivElement>(null)
  const closingRef   = useRef(false)
  const scrollObj    = useRef({ y: 0 })
  const touchStartY  = useRef(0)
  const [currentIdx, setCurrentIdx] = useState(initialIdx)

  const prev = () => setCurrentIdx((i) => Math.max(0, i - 1))
  const next = () => setCurrentIdx((i) => Math.min(items.length - 1, i + 1))

  // Reset scroll when image changes
  useEffect(() => {
    gsap.killTweensOf(scrollObj.current)
    scrollObj.current.y = 0
    if (contentRef.current) contentRef.current.style.transform = "translateY(0px)"
  }, [currentIdx])

  useEffect(() => {
    globalLenis?.stop()
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: "power2.out" })

    // Lenis intercepts wheel events at window-capture and calls preventDefault(),
    // blocking all native overflow scroll. Drive scroll via GSAP transform instead.
    const scrollBy = (delta: number) => {
      const maxY = Math.max(0, (contentRef.current?.scrollHeight ?? 0) - window.innerHeight)
      const target = Math.max(0, Math.min(maxY, scrollObj.current.y + delta))
      gsap.to(scrollObj.current, {
        y: target,
        duration: 0.35,
        ease: "power2.out",
        overwrite: true,
        onUpdate: () => {
          if (contentRef.current) {
            contentRef.current.style.transform = `translateY(-${scrollObj.current.y}px)`
          }
        },
      })
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next()
      if (e.key === "ArrowLeft")  prev()
      if (e.key === "Escape")     handleClose()
      if (e.key === "ArrowDown")  { e.preventDefault(); scrollBy(120) }
      if (e.key === "ArrowUp")    { e.preventDefault(); scrollBy(-120) }
    }
    window.addEventListener("keydown", onKey)

    const onWheel = (e: WheelEvent) => {
      gsap.killTweensOf(scrollObj.current)
      const maxY = Math.max(0, (contentRef.current?.scrollHeight ?? 0) - window.innerHeight)
      scrollObj.current.y = Math.max(0, Math.min(maxY, scrollObj.current.y + e.deltaY))
      if (contentRef.current) {
        contentRef.current.style.transform = `translateY(-${scrollObj.current.y}px)`
      }
    }
    const onTouchStart = (e: TouchEvent)  => { touchStartY.current = e.touches[0].clientY }
    const onTouchMove  = (e: TouchEvent)  => {
      const delta = touchStartY.current - e.touches[0].clientY
      scrollBy(delta)
      touchStartY.current = e.touches[0].clientY
    }

    const overlay = overlayRef.current
    overlay?.addEventListener("wheel",      onWheel,      { passive: true })
    overlay?.addEventListener("touchstart", onTouchStart, { passive: true })
    overlay?.addEventListener("touchmove",  onTouchMove,  { passive: true })

    return () => {
      window.removeEventListener("keydown", onKey)
      overlay?.removeEventListener("wheel",      onWheel)
      overlay?.removeEventListener("touchstart", onTouchStart)
      overlay?.removeEventListener("touchmove",  onTouchMove)
      globalLenis?.start()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    if (closingRef.current) return
    closingRef.current = true
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.2, ease: "power2.in", onComplete: onClose })
  }

  const current = items[currentIdx]
  const url = getImageUrl(current.img, "large")

  return (
    <div
      ref={overlayRef}
      data-gallery-overlay
      className="fixed inset-0 z-50"
      style={{ background: "oklch(0.06 0 0 / 96%)" }}
    >
      {/* Close */}
      <button
        data-overlay-close
        className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white/90"
        style={{ background: "oklch(0.18 0 0)" }}
        onClick={handleClose}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Scrollable image — click outside image to close */}
      <div className="absolute inset-0 overflow-hidden" onClick={handleClose}>
        <div ref={contentRef} className="absolute inset-x-0 top-0" onClick={(e) => e.stopPropagation()}>
          <div className="mx-auto max-w-5xl px-6 py-16">
            {url ? (
              current.img.ext === "webm" || current.img.ext === "mp4" ? (
                <video
                  src={url}
                  className="block h-auto w-full select-none rounded-lg"
                  style={{ filter: "contrast(1.04) brightness(0.94)" }}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={url}
                  alt=""
                  className="block h-auto w-full select-none rounded-lg"
                  style={{ filter: "contrast(1.04) brightness(0.94)" }}
                />
              )
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-white/5">
                <span className="text-xs tracking-widest text-white/20 uppercase">Coming soon</span>
              </div>
            )}
            {/* Feature label below image */}
            <div className="mt-5">
              <p className="text-sm font-medium leading-snug text-foreground/65">
                {PLACEHOLDER_FEATURE.title}
              </p>
              {PLACEHOLDER_FEATURE.description && (
                <p className="mt-1 text-xs leading-relaxed text-foreground/35">
                  {PLACEHOLDER_FEATURE.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Prev / next */}
      {items.length > 1 && (
        <>
          <button
            data-overlay-prev
            className="absolute left-4 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white/90 disabled:opacity-20"
            style={{ background: "oklch(0.18 0 0)" }}
            onClick={prev}
            disabled={currentIdx === 0}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-overlay-next
            className="absolute right-4 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white/90 disabled:opacity-20"
            style={{ background: "oklch(0.18 0 0)" }}
            onClick={next}
            disabled={currentIdx === items.length - 1}
          >
            <ArrowRight className="h-4 w-4" />
          </button>

          {/* Dot indicators */}
          <div data-overlay-dots className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIdx(i)}
                className="h-1 rounded-full transition-all duration-200"
                style={{
                  width: i === currentIdx ? "20px" : "6px",
                  background: i === currentIdx ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── WebCarousel ──────────────────────────────────────────────────────────────

export type CarouselItem = { img: ProjectImage; type: DeviceType }

interface WebCarouselProps {
  items: CarouselItem[]
}

// Placeholder feature shown below each slide until real feature data exists
const PLACEHOLDER_FEATURE = {
  title: "Responsive design",
  description: "Fluid layout adapts from mobile to widescreen with no layout shift.",
}

function WebCarousel({ items }: WebCarouselProps) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const swiperElRef = useRef<HTMLElement | null>(null)

  // Wheel events bubble up to Lenis (which owns window scroll) before Swiper
  // can act on them. Stop propagation at the carousel element so Lenis never
  // sees wheel events while the cursor is over the carousel.
  useEffect(() => {
    const el = swiperElRef.current
    if (!el) return
    const stop = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.stopPropagation()
    }
    el.addEventListener("wheel", stop, { passive: true })
    return () => el.removeEventListener("wheel", stop)
  }, [])

  // Swiper loop needs enough slides to fill ~4× the viewport width on either side.
  // With centeredSlides and variable widths, calculate copies dynamically.
  const loopItems = useMemo(() => {
    const totalWidth = items.reduce((acc, item) => acc + SLIDE_WIDTHS[item.type] + 24, 0)
    const copies = Math.max(2, Math.ceil((1800 * 4) / totalWidth))
    return Array.from({ length: copies }, () => items).flat()
  }, [items])

  return (
    <>
      <style>{`
        [data-web-carousel] .swiper-slide {
          opacity: 0.35;
          transition: opacity 0.5s ease;
        }
        [data-web-carousel] .swiper-slide-active {
          opacity: 1;
        }
        [data-web-carousel] .swiper-slide-active .slide-feature {
          opacity: 1 !important;
        }
      `}</style>

      {/* clip-path clips horizontal overflow while allowing labels to overflow below */}
      <div
        data-web-carousel
        style={{ clipPath: `inset(0 0 -${FEATURE_TEXT_H + 20}px 0)` }}
      >
        <Swiper
          modules={[Autoplay, FreeMode, Mousewheel, Parallax]}
          freeMode={true}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          parallax={true}
          slidesPerView="auto"
          spaceBetween={24}
          autoplay={items.length > 1 ? { delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
          speed={700}
          loop={true}
          centeredSlides={true}
          style={{ overflow: "visible", height: CAROUSEL_H + FEATURE_TEXT_H }}
          onSwiper={(s) => { swiperElRef.current = s.el }}
        >
          {loopItems.map(({ img, type }, idx) => (
            <SwiperSlide
              key={`${img.id}-${idx}`}
              style={{ width: SLIDE_WIDTHS[type] }}
            >
              <div
                className="select-none"
                style={{ cursor: "pointer", height: CAROUSEL_H, transform: "perspective(1200px)" }}
                onClick={() => setExpandedIdx(idx % items.length)}
              >
                <DeviceFrame type={type} images={[img]} activeIdx={0} className="h-full w-full" />
              </div>

              {/* Feature label — parallax-scrolls with the slide, inset to align with image */}
              <div
                className="slide-feature mt-4"
                style={{ opacity: 0.5, transition: "opacity 0.5s ease", paddingLeft: FRAME_PADDING + 2 }}
                data-swiper-parallax="-20%"
              >
                <p className="text-sm font-medium leading-snug text-foreground/65">
                  {PLACEHOLDER_FEATURE.title}
                </p>
                {PLACEHOLDER_FEATURE.description && (
                  <p className="mt-1 text-xs leading-relaxed text-foreground/35">
                    {PLACEHOLDER_FEATURE.description}
                  </p>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {expandedIdx !== null && createPortal(
        <ExpandedOverlay items={items} initialIdx={expandedIdx} onClose={() => setExpandedIdx(null)} />,
        document.body,
      )}
    </>
  )
}

const PLACEHOLDER_ITEMS: CarouselItem[] = [
  { img: { id: "placeholder-desktop", description: null, filename: null, ext: null, tags: [], deviceType: "desktop" }, type: "desktop" },
  { img: { id: "placeholder-mobile",  description: null, filename: null, ext: null, tags: [], deviceType: "mobile"  }, type: "mobile"  },
  { img: { id: "placeholder-tablet",  description: null, filename: null, ext: null, tags: [], deviceType: "tablet"  }, type: "tablet"  },
]

// ─── ProjectGallery ───────────────────────────────────────────────────────────

interface ProjectGalleryProps {
  images: ProjectImage[]
  projectType: string | null
}

export function ProjectGallery({ images, projectType }: ProjectGalleryProps) {
  const isPrint     = Boolean(projectType?.toLowerCase().includes("print"))
  const validImages = images.filter((img) => img.filename && img.ext)

  const items = useMemo<CarouselItem[]>(() => {
    const order: DeviceType[] = ["generic", "desktop", "mobile", "tablet"]
    return validImages
      .filter((img) => img.deviceType != null)
      .map((img) => ({ img, type: img.deviceType as DeviceType }))
      .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type))
  }, [validImages])

  if (isPrint) return <PrintGallery images={validImages} />

  if (validImages.length === 0) {
    return <WebCarousel items={PLACEHOLDER_ITEMS} />
  }

  return <WebCarousel items={items} />
}
